import {
  isUserMessageEvent,
  extractUserText,
} from '@reflex/plugin-chat/web/components/sdk-chat/timeline-helpers';
import { normalizeAgentRunReferences } from '@runloop/reflex-contract';
import { type Agent, type AxonEvent } from '@reflex/shared';
import type { AgentActivity } from '@/lib/types';
import type { PendingUserMessage } from './AgentStreamDataContext';

/** Generic boot-failure error text shown on the synthetic bubble when the agent never made it past `starting`. */
const INITIAL_PROMPT_BOOT_FAILED_MESSAGE = 'Agent failed to start.';

/**
 * Activities that a message still on its way upgrades to `working`. Each one is
 * a live agent that a message starts a turn on; `stopped` / `error` / `starting`
 * are left alone — nothing can be in flight to the first two, and `starting`
 * already reads as working.
 */
const UPGRADABLE_TO_WORKING = new Set<AgentActivity>([
  'idle',
  'waiting_input',
  'interrupted',
  'completed',
]);

/**
 * Fold a message this client has sent but not yet seen echoed back into the
 * activity derived from the stream.
 *
 * Such a message is a turn on its way: the HTTP request may still be in flight,
 * and the server publishes the prompt only once it has a devbox awake enough to
 * take it — minutes, for a suspended box. The stream has nothing to show for
 * that window, so without this the composer reports an idle agent: no Interrupt
 * button, and the next message dispatched on top of the turn instead of queued
 * behind it. The echo-timeout watchdog moves an entry off `sending`, so a
 * message that never lands cannot leave the agent working forever.
 */
export function activityWithPendingSend(
  activity: AgentActivity,
  pending: readonly PendingUserMessage[],
): AgentActivity {
  if (!UPGRADABLE_TO_WORKING.has(activity)) return activity;
  return pending.some((p) => p.status === 'sending') ? 'working' : activity;
}

/**
 * Drop pending entries that match echoed user-message events. FIFO over
 * unmatched events. Every pending status is eligible to resolve: the
 * echo is durable proof that the message reached the agent, even when
 * the HTTP request took long enough for the watchdog to mark the
 * optimistic bubble as `failed` first.
 *
 * Matching is content-equal on the trimmed user text, after collapsing any
 * agent-run references to a stable id token: the optimistic pending entry holds
 * the compact `@[title](agent-run:id)` marker the user typed, but the server
 * expands it into the full `<referenced-agent-run>` transcript before the agent
 * echoes it back, so a raw string compare would never match and the bubble
 * would hang as "unconfirmed". Identical back-to-back messages still resolve in
 * send order because we walk pending oldest-first and consume each unmatched
 * event at most once.
 */
export function reconcilePending(
  events: AxonEvent[] | undefined,
  pending: PendingUserMessage[],
): PendingUserMessage[] {
  if (!events || pending.length === 0) return pending;
  const userTexts: string[] = [];
  const commandTexts: string[] = [];
  for (const e of events) {
    if (e.type === 'codex.command.result') {
      const payload = (e.payload ?? {}) as Record<string, unknown>;
      if (typeof payload.command === 'string') commandTexts.push(payload.command.trim());
      continue;
    }
    if (!isUserMessageEvent(e)) continue;
    const payload = (e.payload ?? {}) as Record<string, unknown>;
    userTexts.push(normalizeAgentRunReferences(extractUserText(payload)).trim());
  }
  if (userTexts.length === 0 && commandTexts.length === 0) return pending;
  const consumed = new Set<number>();
  const consumedCommands = new Set<number>();
  const next = pending.filter((pm) => {
    const target = normalizeAgentRunReferences(pm.text).trim();
    for (let i = 0; i < commandTexts.length; i++) {
      if (consumedCommands.has(i)) continue;
      if (commandTexts[i] === target) {
        consumedCommands.add(i);
        return false;
      }
    }
    for (let i = 0; i < userTexts.length; i++) {
      if (consumed.has(i)) continue;
      if (userTexts[i] === target) {
        consumed.add(i);
        return false;
      }
    }
    return true;
  });
  return next.length === pending.length ? pending : next;
}

/**
 * Strip heavy binary `data` from attachment metadata so a failed
 * pending entry can persist in `localStorage` without blowing the
 * quota. The server keeps the original upload, so re-display only
 * needs name + mimeType.
 */
export function stripAttachmentBlobs(
  attachments: PendingUserMessage['attachments'],
): PendingUserMessage['attachments'] {
  if (!attachments) return undefined;
  return attachments.map((a) => ({ name: a.name, mimeType: a.mimeType }));
}

/**
 * Decide what status a `'sending'` pending entry should transition to
 * when its echo-timeout deadline elapses without resolution.
 *
 * - HTTP send was acknowledged (server returned 2xx) → `unconfirmed`:
 *   the message reached the server / Axon publish succeeded, but no
 *   echoed event arrived. Probably delivered.
 * - HTTP send not acked → `failed`: no evidence of delivery.
 *
 * Entries already in `failed` / `unconfirmed` are returned untouched.
 */
export function nextStatusOnTimeout(pending: PendingUserMessage): 'unconfirmed' | 'failed' | null {
  if (pending.status !== 'sending') return null;
  return pending.httpAcked ? 'unconfirmed' : 'failed';
}

/**
 * Build a synthetic `PendingUserMessage` from `agent.prompt` so a
 * booting agent's initial prompt renders as an optimistic user bubble
 * before the agent has echoed it back through the live stream. The
 * bubble auto-resolves through the same `reconcilePending` path as a
 * real send once the matching `query` / `session/prompt` event arrives.
 *
 * Returns `null` (no synthetic) when:
 * - `agent.prompt` is empty.
 * - The stream already contains any user-message echo — the prompt has
 *   either been processed or the agent has already taken at least one
 *   user input, so suppressing avoids a duplicate bubble for legacy /
 *   already-running agents.
 * - The agent is in a terminal post-run state (`stopped`, `completed`,
 *   `terminated`) with no echo — treat as legacy and surface no
 *   synthetic to avoid a bubble stuck in `'sending'` forever.
 *
 * Otherwise:
 * - `agent.status === 'error'` → synthetic in `'failed'` state with a
 *   generic boot-failure message. The existing `agent.error` event UI
 *   surfaces the actual error details separately.
 * - All other (still-active) statuses → synthetic in `'sending'` state.
 *
 * Synthetic entries are recomputed each render and never persist.
 */
export function getInitialPromptPending(
  agent: Pick<Agent, 'id' | 'prompt' | 'status' | 'createdAt' | 'userId'>,
  events: AxonEvent[] | undefined,
): PendingUserMessage | null {
  const prompt = agent.prompt?.trim();
  if (!prompt) return null;
  if (events?.some(isUserMessageEvent)) return null;
  if (agent.status === 'stopped' || agent.status === 'completed' || agent.status === 'terminated') {
    return null;
  }
  const isError = agent.status === 'error';
  return {
    clientId: `initial-${agent.id}`,
    text: agent.prompt,
    createdAt: agent.createdAt,
    status: isError ? 'failed' : 'sending',
    error: isError ? INITIAL_PROMPT_BOOT_FAILED_MESSAGE : undefined,
    synthetic: true,
    userId: agent.userId ?? undefined,
  };
}

/**
 * Human-readable reason for a mailbox command that reached `failed`,
 * keyed by the server's stable failure code. Unknown codes fall back to
 * the server's detail text, then to a generic line that still names the
 * code so support can join it to logs.
 */
export function describeCommandFailure(failure: {
  code: string;
  detail?: string;
  statusCode?: number;
}): string {
  switch (failure.code) {
    case 'agent_terminal':
      return 'The agent has stopped and cannot take new messages.';
    case 'claim_expired':
      return 'The agent did not pick up the message in time. It was not delivered.';
    case 'dispatch_failed':
      return failure.detail
        ? `The agent could not accept the message: ${failure.detail}`
        : 'The agent could not accept the message.';
    default:
      return failure.detail ?? `The message was not delivered (${failure.code}).`;
  }
}
